import { Button, Checkbox, ComboboxItem, Grid, Group, Radio, Select, Text, TextInput, useMantineTheme } from '@mantine/core';
import React, { ChangeEvent, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { getPono } from '../../api/poList';
import AllocationTable from '../../components/AllocationTable/AllocationTable';
import InvoiceTable from '../../components/InvoiceTable/InvoiceTable';
import NavBar from '../../components/NavBar/NavBar';
import StyleTable from '../../components/TransitTable/StyleTable';
import { DOORS, STORES, STORES_PAIRS } from '../../constants/stores';
import { useDebounce } from '../../Hooks/useDebounce';
import { PoNoWithStatus } from '../../interface/AllocationInvoice';
import { AllocationData, StoreData } from '../../interface/AllocationTable';
import { StyleTableData } from '../../interface/TransitTable';
import { allocationPaperPDF } from '../../PDFs/allocationPaper';
import { sizeAllocationPDF } from '../../PDFs/sizeAllocation';
import { FEATURE_FLAGS } from '../../Utils/utils';
import classes from './Allocation.module.css';

const AllocationCalculator: React.FC = () => {
    const theme = useMantineTheme();
    const token = useSelector((state: any) => state.user.sessionId);
    const [poNoList, setPoNoList] = useState<PoNoWithStatus[]>([]);
    const [po, setPo] = useState<{ poNo: string, poId: string }>();
    const [stores, setStores] = useState<{ store: string, checked: boolean }[]>(STORES.map((store: string) => ({ store: store, checked: true })));
    const [sizes, setSizes] = useState<string[]>([]);
    const [totalProductionQuantities, setTotalProductionQuantities] = useState<number[]>([]);
    const [standardQuantities, setStandardQuantities] = useState<number[]>([]);
    const [totalProductionSum, setTotalProductionSum] = useState<number>(0);
    const [calculateClicked, setCalculateClicked] = useState(false);
    const [calculateBtnClicked, setCalculateBtnClicked] = useState(false);
    const [tableData, setTableData] = useState<StoreData[]>([]);
    const [message, setMessage] = useState('');
    const [isStyleLoading, setIsStyleLoading] = useState(false);
    const [supplierList, setSupplierList] = useState<{ label: string, value: string }[]>([]);
    const [poSearchTerm, setPoSeachTerm] = useState('');
    const [supplierSearchTerm, setSupplierSearchTerm] = useState('');
    const poSearch = useDebounce(poSearchTerm, 500);
    const supplierSearch = useDebounce(supplierSearchTerm, 500);
    const [styleTableData, setStyleTableData] = useState<StyleTableData[]>([]);
    const [poTableData, setPoTableData] = useState<any>(null);
    const [styleMessage, setStyleMessage] = useState('');
    const [styleNo, setStyleNo] = useState('');
    const [overstockClicked, setOverstockClicked] = useState(false);
    const [prePacked, setPrePacked] = useState(false);
    const [packed, setPacked] = useState<number[]>([]);
    const [osBox, setOsBox] = useState(false);
    const [osBoxPacked, setOsBoxPacked] = useState<number[]>([]);
    const [allocation, setAllocation] = useState<
        AllocationData>({
            poNo: '',
            poId: '',
            supplierName: '',
            styleNo: '',
            cost: '',
            msrp: '',
            notes: '',
            binLocation: '',
            description: '',
            color: ''
        });


    const getStyle = async (styleNo: string, event?: any) => {
        if (!styleNo || styleNo === '') return;
        try {
            setIsStyleLoading(true);
            let url = `/style/rms/styleInfo?style_no=${styleNo}`;
            if (po?.poNo) {
                url += `&po_no=${encodeURIComponent(po.poNo)}`;
            }
            const response = await fetch(url, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                }
            });
            const { data, message } = await response.json();
            if (response.status === 200) {
                setStyleTableData(data.styleTable);
                setAllocation(prev => ({
                    ...prev,
                    color: data.style.color?.toUpperCase() || allocation.color,
                    description: data.style.description?.toUpperCase() || allocation.description,
                    binLocation: data.style.binLocation?.toUpperCase() || allocation.binLocation,
                }));
                if (data.style.message) {
                    setStyleMessage(data.style.message);
                } else {
                    setStyleMessage('');
                }
            }
            if (response.status === 201) {
                setStyleTableData([]);
                setAllocation(prev => ({
                    ...prev,
                    color: '',
                    description: '',
                    binLocation: ''
                }));
            }
            if (response.status === 400 && event) {
                event.target.focus();
                event.preventDefault();
                alert(message);
                setAllocation(prev => ({ ...prev, styleNo: '' }));
                getTableData(po?.poId || '');
            }
            setIsStyleLoading(false);
        } catch (error) {
            console.log('Error getting style data:', error)
            setIsStyleLoading(false);
        }
    };

    const getPonoData = async (searchTerm?: string) => {
        try {
            const response = await getPono(token, searchTerm || '');
            const data = response?.data;
            if (data === null) {
                setPoNoList([{ poNo: `+ Create ${searchTerm?.toUpperCase() || ''}` }]);
                return;
            }
            setPoNoList([...data, { poNo: `+ Create ${searchTerm?.toUpperCase() || ''}` }]);
        } catch (error) {
            console.log(error);
        }
    }

    const getSuppliers = async (searchTerm?: string) => {
        if (!searchTerm || searchTerm === '') return;
        try {
            const response = await fetch(`/supplier/getAllsuppliers?searchTerm=${searchTerm}&limit=5&page=1`, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
            });
            if (response.status === 204 || response.status === 304) {
                setSupplierList(prev => [
                    ...prev,
                    {
                        label: `+ Create ${searchTerm?.toUpperCase() || ''}`,
                        value: `+ Create ${searchTerm?.toUpperCase() || ''}`
                    }
                ]);
            }
            if (response.status === 200) {
                const { data, message } = await response.json();
                setSupplierList(data.map((item: any) => ({ label: item.supplier_name, value: item.supplier_name })));
            }
        } catch (error) {
            console.log('Error getting suppliers:', error)
        }
    }

    const createSupplier = async (supplierName: string) => {
        try {
            const response = await fetch('/supplier/create', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({ supplierName })
            });
            const { data, message } = await response.json();
            if (response.status === 200) {
                setSupplierList(prev => [
                    ...prev,
                    { label: data, value: data }
                ]);
                setAllocation(prev => ({ ...prev, supplierName: data }));
            }
        } catch (error) {
            console.log('Error creating supplier:', error);
            return null;
        }
    }

    useEffect(() => {
        getPonoData(poSearchTerm);
    }, [poSearch]);

    useEffect(() => {
        getSuppliers(supplierSearchTerm);
    }, [supplierSearch])

    const handlePoNoChange = (value: string | null, option: ComboboxItem) => {
        if (value?.startsWith('+ Create ')) {
            const poNumber = value.split('+ Create ')[1].toUpperCase() ?? '';
            //check if po number alredy exists in the poNoList
            if (poNoList.some((item: any) => item.poNo === poNumber)) {
                // set the existing po number and do not add it to the poNoList
                setPo({ poNo: poNumber, poId: poNoList.find((item: any) => item.poNo === poNumber)?.poId?.toString() || '' })
                return;
            } else {
                setPo({ poNo: poNumber, poId: '' });
                setPoNoList(prev => [
                    ...prev,
                    { poNo: poNumber, poId: 0 }
                ]);
            }
        } else {
            let selectedPo = { poNo: value ?? '', poId: poNoList.find((item) => item.poNo === value)?.poId?.toString() || '' }
            setPo(selectedPo);
            getTableData(selectedPo.poId);
        }
    }

    const handleSupplierChange = async (value: string | null, option: ComboboxItem) => {
        if (value?.startsWith('+ Create ')) {
            let supplier = value.split('+ Create ')[1].toUpperCase() ?? '';
            await createSupplier(supplier);
        } else {
            setAllocation(prev => ({
                ...prev,
                supplierName: value || ''
            }));
        }
    }

    const handleSearchChange = (value: string) => {
        setPoSeachTerm(value.toUpperCase());
    }

    const handleSupplierSearch = (value: string) => {
        setSupplierSearchTerm(value.toUpperCase());
    }

    const handleStyleChange = async (event: ChangeEvent<HTMLInputElement>) => {
        const style_no = event.target.value?.trim().toUpperCase();
        if (poTableData?.styles?.length > 0) {
            if (poTableData?.styles?.includes(style_no)) {
                event.target.focus();
                event.preventDefault();
                alert('Style already exists in this PO. Please check the style number again.');
                setAllocation(prev => ({ ...prev, styleNo: '' }));
                return;
            }
        }
        // check if the style number is already in the poTableData
        if (styleNo !== style_no) {
            getStyle(style_no, event);
        }
        setStyleNo(style_no);
    };

    const handleReset = async (skipSaveConfirmation = false) => {
        if (!skipSaveConfirmation) {
            const confirmReset = window.confirm("Do you want to reset the form?");
            if (confirmReset) {
                onSave();
                return;
            } else {
                return;
            }
        } else {
            // reset the table data to an initial state with all the quantity of the sizes set to 0
            setTableData(stores.filter((store) => store.checked).map((store) => ({
                storeName: store.store, // Use the store property directly
                sizeQuantities: sizes.map((size) => ({
                    size,
                    quantity: 0
                }))
            })));
            setAllocation(prev => ({
                ...prev,
                styleNo: '',
                color: '',
            }));
            // set total production quantities to 0 for each index
            setTotalProductionQuantities(Array(sizes.length).fill(0));
            setStandardQuantities(Array(stores.length).fill(0));
        }
    };

    const handleCheckAll = (checked: boolean) => {
        if (checked) {
            setStores(STORES.map((store: string) => ({ store: store, checked: true })));
        } else {
            setStores(STORES.map((store: string) => ({ store: store, checked: false })));
        }
    };

    const handleSizeChange = (event: ChangeEvent<HTMLInputElement>) => {
        const value = event.target.value.toUpperCase();
        let newSizes = value.split(',');
        setSizes(value ? newSizes : []);
        if (osBox) { setOsBoxPacked(new Array(newSizes.length).fill(0)) }
        setTotalProductionQuantities(newSizes.map((size: string, index: number) => totalProductionQuantities[index] || 0));
    }

    const handleCalculateClick = () => {
        setCalculateBtnClicked(!calculateBtnClicked);
        setCalculateClicked(true);
    };

    const calculateTotalAllocation = (sizeIndex: number) => {
        return tableData.reduce(
            (total, row) => {
                // Check if row and row.sizeQuantities are not null
                if (row && row.sizeQuantities) {
                    // Check if sizeIndex is within the bounds of the sizeQuantities array
                    if (sizeIndex < row.sizeQuantities.length) {
                        return total + row.sizeQuantities[sizeIndex].quantity;
                    }
                }
                return total;
            }, 0
        );
    };

    const handleOsBoxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.checked) setOsBoxPacked(new Array(sizes.length).fill(0));
        setOsBox(e.target.checked);
    }

    const handleOsPackedBoxChanges = (e: React.ChangeEvent<HTMLInputElement>, index: number) => {
        const value = e.target.value || '0';
        // set element at specific index
        setOsBoxPacked((prev) => {
            const newPacked = [...prev];  // Create a copy of the previous packed array
            newPacked[index] = parseInt(value, 10);  // Set the value at the specified index
            return newPacked;  // Return the new array
        });
    }

    const getTableData = async (poId: string) => {
        try {
            const response = await fetch('/po/poDetails', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`,
                },
                body: JSON.stringify({ poId: poId }),
            });
            if (response.status !== 200) {
                setPoTableData(null);
            }
            const data = await response.json();
            setPoTableData(data);
        } catch (error) {
            console.log('Error fetching invocie data: ', error);
        }
    }

    const calculateOverstock = (sizeIndex: number) => {
        const totalAllocation = calculateTotalAllocation(sizeIndex);
        const totalProductionQuantity = totalProductionQuantities[sizeIndex] || 0;
        const result = Math.max(totalProductionQuantity - totalAllocation);
        return isNaN(result) ? 0 : result;
    };

    function mergeStoreData(tableData: any[], storeData: Record<string, any>) {
        // Clone the storeData to avoid mutating the original
        const mergedData = { ...storeData };
        tableData.forEach(({ storeName, sizeQuantities }) => {
            const sumQuantities = sizeQuantities.reduce((sum: number, st: { quantity: number }) => sum + st.quantity, 0);
            if (mergedData[storeName]) {
                // If store exists, add to totalQuantity
                mergedData[storeName].totalStyles += 1;
                mergedData[storeName].totalQuantity += sumQuantities;
            } else {
                // If store doesn't exist, add a new entry
                mergedData[storeName] = {
                    totalStyles: 1,
                    totalQuantity: sumQuantities
                };
            }
        });
        return mergedData;
    }


    const onSave = async () => {
        try {
            let tempPoId = allocation?.poId;
            if (allocation?.poId === null || allocation?.poId === undefined) {
                setPo(prev => ({
                    ...prev,
                    poId: allocation?.poNo?.toUpperCase()?.toString() || '',
                    poNo: allocation?.poNo || ''
                }));
                tempPoId = allocation?.poId;
            }
            if (!allocation?.supplierName ||
                !allocation?.styleNo ||
                !allocation?.cost ||
                !allocation?.msrp ||
                allocation?.supplierName.trim() === '' ||
                allocation?.styleNo.trim() === '' ||
                allocation?.cost.trim() === '' ||
                allocation?.msrp.trim() === '' ||
                sizes.length === 0 ||
                totalProductionQuantities.length === 0
            ) {
                alert('Please fill in all required fields');
                return;
            }
            if (totalProductionQuantities.length !== sizes.length) {
                alert('Please enter appropriate quantity for each size');
                return;
            }
            const statusArray = Array(tableData.length).fill(false);
            const initialArray = Array(tableData.length).fill('SVP');
            const data = {
                poNo: po?.poNo,
                po_id: tempPoId,
                supplier_name: allocation?.supplierName,
                styleNo: allocation?.styleNo.trim(),
                description: allocation?.description,
                color: allocation?.color,
                cost: allocation?.cost,
                msrp: allocation?.msrp,
                notes: allocation?.notes,
                status: statusArray,
                binlocation: allocation?.binLocation?.toUpperCase() || '',
                storeName: tableData.map(data => data.storeName),
                sizeQuantities: tableData.map(data => data.sizeQuantities),
                receivedQty: sizes.map((size, index) => ({
                    size: size,
                    quantity: totalProductionQuantities[index],
                })),
                total: totalProductionQuantities.reduce((a, b) => a + b, 0),
                totalAllocationPerSize: sizes.map((_size, index) => calculateTotalAllocation(index)),
                overstockPerSize: sizes.map((_size, index) => calculateOverstock(index)),
                // assuming that allocationId is available
                initial: initialArray,
                order_status: 'allocation',
            };
            const response = await fetch('/allocation/create', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify(data)
            });
            const responseData = await response.json();
            if (response.status === 200) {
                getTableData(responseData.data.po_id);
                setPo({ poId: responseData.data.po_id, poNo: po?.poNo || '' });
                alert('Data saved successfully');
                handleReset(true); // Pass true to skip save confirmation
            } else if (response.status === 400) {
                alert(responseData.message);
                setMessage('Error while saving data');
            } else {
                alert(message ? message : 'Error while saving data');
            }
        } catch (error: any) {
            setMessage('Error while saving data: ' + error.message);
        }
    };

    const downloadAllPDFs = async () => {
        if (sizes.length === 0 || tableData.length === 0 || totalProductionQuantities.length === 0) {
            alert('Please enter appropriate quantity for each size');
            return;
        }
        await allocationPaperPDF(
            sizes,
            tableData,
            totalProductionQuantities,
            totalProductionSum,
            allocation,
            po?.poNo || '',
            poTableData?.styles?.length + 1
        );
        await sizeAllocationPDF(sizes, 6, tableData, allocation.styleNo);
    }

    return (
        <div>
            <NavBar />
            <div className={classes.main}>
                <div>
                    <Grid>
                        <Grid.Col span={{ xs: 12, sm: 12, md: 3, xl: 3 }} className={classes.gridColDiv}>
                            <div style={{ marginTop: '30px', height: '35vh', overflow: 'auto', marginBottom: '10px' }}>
                                <StyleTable styleData={styleTableData} style_no={allocation?.styleNo} message={styleMessage} isStyleLoading={isStyleLoading} />
                            </div>
                        </Grid.Col>
                        <Grid.Col span={{ xs: 12, sm: 12, md: 6, xl: 6 }} className={classes.gridColDiv}>
                            <h2 className={'title'} style={{ marginTop: '30px' }}>Allocation</h2>
                            <div className={classes.gridRowDiv} >
                                <Text className={classes.textWithInput}>Po No:</Text>
                                <Select
                                    data={Array.from({ length: poNoList.length }, (_, index) => poNoList[index].poNo)}
                                    placeholder="Select Po No"
                                    style={{ flexGrow: 1 }}
                                    value={po?.poNo || null}
                                    searchable
                                    clearable
                                    onChange={handlePoNoChange}
                                    onSearchChange={handleSearchChange}
                                />
                            </div>
                            <Grid>
                                <Grid.Col span={{ xs: 12, sm: 6, md: 6, xl: 6 }} className={classes.gridColDiv}>
                                    <div className={classes.gridRowDiv} >
                                        <Text className={classes.textWithInput}>Supplier:</Text>
                                        <Select
                                            data={supplierList}
                                            style={{ flexGrow: 1 }}
                                            searchable
                                            clearable
                                            value={allocation.supplierName}
                                            onChange={handleSupplierChange}
                                            onSearchChange={handleSupplierSearch}
                                        />
                                    </div>
                                    <div className={classes.gridRowDiv}>
                                        <Text className={classes.textWithInput}>Style No:</Text>
                                        <TextInput
                                            style={{ flexGrow: 1 }}
                                            value={allocation?.styleNo}
                                            onChange={(e) => setAllocation(prev => ({ ...prev, styleNo: e.target.value?.toUpperCase() }))}
                                            onBlur={handleStyleChange}
                                        />
                                    </div>
                                    <div className={classes.gridRowDiv} >
                                        <Text className={classes.textWithInput}>Description:</Text>
                                        <TextInput style={{ flexGrow: 1 }} value={allocation?.description} onChange={(e) => setAllocation(prev => ({ ...prev, description: e.target.value.toUpperCase() }))} />
                                    </div>
                                    <div className={classes.gridRowDiv} >
                                        <Text className={classes.textWithInput}>Notes:</Text>
                                        <TextInput style={{ flexGrow: 1 }} value={allocation?.notes} onChange={(e) => setAllocation(prev => ({ ...prev, notes: e.target.value.toUpperCase() }))} />
                                    </div>
                                </Grid.Col>
                                <Grid.Col span={{ xs: 12, sm: 6, md: 6, xl: 6 }} className={classes.gridColDiv}>
                                    <div className={classes.gridRowDiv} >
                                        <Text className={classes.textWithInput}>Color:</Text>
                                        <TextInput style={{ flexGrow: 1 }} value={allocation?.color} onChange={(e) => setAllocation(prev => ({ ...prev, color: e.target.value.toUpperCase() }))} />
                                    </div>
                                    <div className={classes.gridRowDiv} >
                                        <Text className={classes.textWithInput}>Cost:</Text>
                                        <TextInput style={{ flexGrow: 1 }} value={allocation?.cost} onChange={(e) => setAllocation(prev => ({ ...prev, cost: e.target.value }))} />
                                    </div>
                                    <div className={classes.gridRowDiv} >
                                        <Text className={classes.textWithInput}>MSRP:</Text>
                                        <TextInput style={{ flexGrow: 1 }} value={allocation?.msrp} onChange={(e) => setAllocation(prev => ({ ...prev, msrp: e.target.value }))} />
                                    </div>
                                    <div className={classes.gridRowDiv} >
                                        <Text className={classes.textWithInput}>BIN:</Text>
                                        <TextInput style={{ flexGrow: 1 }} value={allocation?.binLocation} onChange={(e) => setAllocation(prev => ({ ...prev, binLocation: e.target.value }))} />
                                    </div>
                                </Grid.Col>
                            </Grid>
                        </Grid.Col>
                        <Grid.Col span={{ xs: 12, sm: 12, md: 3, xl: 3 }} className={classes.gridColDiv}>
                            <div className={classes.gridColDiv} style={{ marginTop: '30px', height: '35vh', overflow: 'auto', marginBottom: '10px' }}>
                                <InvoiceTable data={poTableData} />
                            </div>
                        </Grid.Col>
                    </Grid>
                </div>
                <div className={classes.sizeStoreDiv} style={{ backgroundColor: theme.colors.gray[1], color: theme.colors.gray[10] }}>
                    <div className={classes.rowDiv} >
                        <Text className={classes.textWithInput}>Sizes:</Text>
                        <TextInput size='xs' style={{ flexGrow: 1 }} value={sizes} onChange={handleSizeChange} />
                    </div>
                    {FEATURE_FLAGS.OS_BOX_FLAG && <div className={classes.rowDiv} >
                        <Text className={classes.textWithInput} style={{ width: '100px' }}>Qty in box:</Text>
                        <Checkbox className={classes.inputElement} checked={osBox} onChange={(e) => handleOsBoxChange(e)} color={theme.colors.gray[9]} />
                        {osBoxPacked.length > 0 && osBox && <div className={classes.rowDiv}>
                            {osBoxPacked.map((item: number, index: number) => (
                                <TextInput type='number' value={item} onChange={(e) => handleOsPackedBoxChanges(e, index)} />
                            ))}
                        </div>}
                    </div>}
                    <div className={classes.rowDiv} >
                        <Text className={classes.textWithInput}>Stores:</Text>
                        <div style={{ flexGrow: 1 }} >
                            <Group className={classes.checkboxGroup}  >
                                <Checkbox color={theme.colors.gray[8]} defaultChecked value="checkall" label="Check All" onChange={(event) => handleCheckAll(event.currentTarget.checked)} />
                                {STORES.map((store) => <Checkbox
                                    color={theme.colors.gray[8]}
                                    key={store}
                                    label={STORES_PAIRS[store]}
                                    checked={stores.find((item) => item.store === store)?.checked}
                                    onChange={(event) => {
                                        setStores((prev) => prev.map((item) => item.store === store ? { ...item, checked: !item.checked } : item))
                                    }}
                                    value={store}
                                />)}
                            </Group>
                        </div>
                    </div>
                    <div className={classes.rowDiv}>
                        <Text className={classes.textWithInput}>Select Door:</Text>
                        <div style={{ flexGrow: 1 }} >
                            <Group className={classes.checkboxGroup}>
                                {Object.keys(DOORS).map((door) => (
                                    <Radio
                                        color={theme.colors.gray[8]}
                                        key={door}
                                        label={door}
                                        name="doors" // important: group radios by name
                                        onChange={(event) => {
                                            const storeListDoor = DOORS[door];

                                            if (event.currentTarget.checked) {
                                                setStores((prev) =>
                                                    prev.map((item) => ({
                                                        ...item,
                                                        checked: storeListDoor.includes(item.store),
                                                    }))
                                                );
                                            } else {
                                                setStores((prev) =>
                                                    prev.map((item) => ({
                                                        ...item,
                                                        checked: false,
                                                    }))
                                                );
                                            }
                                        }}
                                    />
                                ))}
                            </Group>
                        </div>
                    </div>
                </div>
                <div>
                    <AllocationTable
                        stores={stores}
                        sizes={sizes}
                        totalProductionQuantities={totalProductionQuantities}
                        setTotalProductionQuantities={setTotalProductionQuantities}
                        standardQuantities={standardQuantities}
                        setStandardQuantities={setStandardQuantities}
                        tableData={tableData}
                        setTableData={setTableData}
                        calculateClicked={calculateClicked}
                        setCalculateClicked={setCalculateClicked}
                        calculateBtnClicked={calculateBtnClicked}
                        totalProductionSum={totalProductionSum}
                        setTotalProductionSum={setTotalProductionSum}
                        overstockClicked={overstockClicked}
                        prePacked={prePacked}
                        osBoxPacked={osBoxPacked}
                        osBox={osBox}
                        requiredAdjustment={'OS'}
                    />
                </div>
                <div className={classes.buttonDiv}>
                    <Button style={{ backgroundColor: theme.colors.gray[9] }} onClick={handleCalculateClick} >Calculate</Button>
                    <Button style={{ backgroundColor: theme.colors.gray[9] }} onClick={() => handleReset()} >Reset</Button>
                    <Button style={{ backgroundColor: theme.colors.gray[9] }} onClick={onSave} >Save</Button>
                    <Button style={{ backgroundColor: theme.colors.gray[9] }} onClick={downloadAllPDFs}>Print</Button>
                    <Button style={{ backgroundColor: theme.colors.gray[9] }} onClick={() => setOverstockClicked(!overstockClicked)}>Send to Steeles</Button>
                </div>
            </div>
        </div>
    );
}


export default AllocationCalculator;